// src/components/Help/HelpPage.jsx

import React, { useState, useMemo, useContext } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
    Box, Typography, Container, Grid, Accordion, AccordionSummary, AccordionDetails, TextField, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Avatar, InputAdornment, List, ListItem,
    ListItemIcon, ListItemText, ListItemButton, Stack, Card, CardContent, Divider, Tooltip, MenuItem
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { helpContent as content } from './helpContent';
import { useSystemStatus } from '../../hooks/useSystemStatus';
import { useAuth } from '../../hooks/useAuth';
import { submitSupportTicket } from '../../ctapi/supportService'; 
import SpinningCustomLogo from './../Common/SpinningCustomLogo'; 
import MyTickets from './myTickets';
import ModalContext from '../../shared/ModalContext/ModalContext';

// Add 'type' to the validation schema
const TicketSchema = Yup.object().shape({
    type: Yup.string().required('Please select a ticket type.'),
    subject: Yup.string().min(5, 'Too Short!').required('Required'),
    description: Yup.string().min(20, 'Please provide more detail.').required('Required'),
});

const iconMap = {
    RocketLaunchIcon: <RocketLaunchIcon />,
    ReceiptLongIcon: <ReceiptLongIcon />,
    PersonAddIcon: <PersonAddIcon />,
};

// These dialogs are exported to be used by the central ModalProvider
export const InfoDialog = ({ open, onClose, data }) => {
    if (!data) return null;
    const { title, body, icon } = data;
    const DialogIcon = icon ? React.cloneElement(iconMap[icon], { sx: { fontSize: 28 }}) : null;

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: '440px' } }}>
            <Box sx={{ p: 3, pt: 4, textAlign: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>{DialogIcon}</Avatar>
                <DialogTitle sx={{ p: 0, pb: 1, fontWeight: 600 }}>{title}</DialogTitle>
                <DialogContent sx={{ p: 0 }}><Typography color="text.secondary">{body}</Typography></DialogContent>
            </Box>
            <DialogActions sx={{ justifyContent: 'center', p: 2, pt: 1 }}>
                <Button onClick={onClose} variant="contained" sx={{ borderRadius: '20px', px: 3 }}>Got it</Button>
            </DialogActions>
        </Dialog>
    );
};

export const SuccessDialog = ({ open, onClose, ticket_Id }) => {
    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: '440px' } }}>
            <Box sx={{ p: 3, pt: 4, textAlign: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main', width: 56, height: 56, mx: 'auto', mb: 2 }}><MarkEmailReadIcon sx={{ fontSize: 28 }} /></Avatar>
                <DialogTitle sx={{ p: 0, pb: 1, fontWeight: 600 }}>Ticket Submitted!</DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Typography color="text.secondary">Your support ticket has been received. Your reference number is #{ticket_Id}. Our team will get back to you shortly.</Typography>
                </DialogContent>
            </Box>
            <DialogActions sx={{ justifyContent: 'center', p: 2, pt: 1 }}>
                <Button onClick={onClose} variant="contained" color="success" sx={{ borderRadius: '20px', px: 3 }}>Awesome!</Button>
            </DialogActions>
        </Dialog>
    );
};

const HelpPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const { statuses, isLoading: isStatusLoading } = useSystemStatus();
    const { user } = useAuth();
    const { openModal } = useContext(ModalContext);

    const filteredFaqs = useMemo(() => {
        if (!content?.faq?.categories) return [];
        if (!searchQuery) return content.faq.categories;
        return content.faq.categories.map(category => ({
            ...category,
            questions: category.questions.filter(
                faq => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
            )
        })).filter(category => category.questions.length > 0);
    }, [searchQuery]);
    
    const handleOpenInfoDialog = (data) => {
        openModal('HELP_INFO', { data });
    };
    
    const handleApiSubmit = async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        try {
            const response = await submitSupportTicket(values, user);
            openModal('HELP_SUCCESS', { ticket_Id: response.ticket_id });
            resetForm();
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };
    
    const handleEmailSubmit = (values) => {
        const to = "helpdesk@candidatetools.com";
        const subject = encodeURIComponent(`[${values.type}] ${values.subject}`);
        const body = encodeURIComponent(`Description:\n${values.description}`);
        window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    };

    if (!content) return null; 

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>{content.pageTitle}</Typography>
            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <Stack spacing={4}>
                        <MyTickets />
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 500, mb: 2 }}>{content.systemStatus.title}</Typography>
                                {isStatusLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}><SpinningCustomLogo size={40} /></Box>
                                ) : (
                                    <List disablePadding>
                                        {statuses.map(service => (
                                            <ListItem key={service.id} disablePadding sx={{ py: 1 }}>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    {service.status === 'Operational' ? <CheckCircleIcon color="success" sx={{ fontSize: 20 }} /> : <ErrorIcon color="error" sx={{ fontSize: 20 }} />}
                                                </ListItemIcon>
                                                <ListItemText primary={service.name} />
                                                <Typography variant="body2" color={service.status === 'Outage' ? 'error.main' : 'text.secondary'}>{service.status}</Typography>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 500, mb: 2 }}>{content.quickLinks.title}</Typography>
                                <List disablePadding>
                                    {content.quickLinks.links.map(link => (
                                        <ListItemButton
                                            key={link.id}
                                            component={link.type === 'navigate' ? RouterLink : 'button'}
                                            to={link.path}
                                            state={link.state}
                                            onClick={link.type === 'dialog' ? () => handleOpenInfoDialog(link.content) : undefined}
                                            sx={{ borderRadius: 2, textAlign: 'left', p: 1.5 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 48 }}>{iconMap[link.icon]}</ListItemIcon>
                                            <ListItemText primary={link.title} secondary={link.description} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Stack spacing={4}>
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p:0 }}>
                                <Box sx={{ p: 3, display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                                    <QuestionAnswerIcon color="primary" sx={{ mr: 1.5 }} />
                                    <Typography variant="h5" component="h2" sx={{ fontWeight: 500 }}>{content.faq.title}</Typography>
                                </Box>
                                <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                                    <TextField fullWidth variant="outlined" placeholder="Search questions..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),}}/>
                                </Box>
                                <Box>
                                    {filteredFaqs.length > 0 ? (
                                        filteredFaqs.map(category => (
                                            <Box key={category.id}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, px: 3, py: 2, bgcolor: 'action.hover', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{category.name}</Typography>
                                                {category.questions.map((faq) => (
                                                    <Accordion key={faq.id} elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' }, '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider'} }}>
                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontWeight: 500 }}>{faq.question}</Typography></AccordionSummary>
                                                        <AccordionDetails><Typography color="text.secondary">{faq.answer}</Typography></AccordionDetails>
                                                    </Accordion>
                                                ))}
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No results found for "{searchQuery}"</Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
                                    <SupportAgentIcon color="primary" sx={{ mr: 1.5 }} />
                                    <Box>
                                        <Typography variant="h5" component="h2" sx={{ fontWeight: 500 }}>{content.ticket.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{content.ticket.description}</Typography>
                                    </Box>
                                </Box>
                                <Divider />
                                <Formik 
                                    initialValues={{ type: 'General Inquiry', subject: '', description: '' }} 
                                    validationSchema={TicketSchema} 
                                    onSubmit={handleApiSubmit}
                                >
                                    {({ errors, touched, isSubmitting, values }) => (
                                        <Form>
                                            <Box sx={{ p: 3 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field 
                                                            as={TextField} 
                                                            required 
                                                            fullWidth 
                                                            name="type" 
                                                            label="Ticket Type" 
                                                            select
                                                            error={touched.type && Boolean(errors.type)} 
                                                            helperText={touched.type && errors.type}
                                                        >
                                                            <MenuItem value="General Inquiry">General Inquiry</MenuItem>
                                                            <MenuItem value="Bug Report">Bug Report</MenuItem>
                                                            <MenuItem value="Billing Question">Billing Question</MenuItem>
                                                            <MenuItem value="Feature Request">Feature Request</MenuItem>
                                                        </Field>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field as={TextField} required fullWidth name="subject" label={content.ticket.subjectLabel} error={touched.subject && Boolean(errors.subject)} helperText={touched.subject && errors.subject}/>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Field as={TextField} required fullWidth name="description" label={content.ticket.descriptionLabel} multiline rows={6} error={touched.description && Boolean(errors.description)} helperText={touched.description && errors.description}/>
                                                    </Grid>
                                                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Button type="submit" disabled={isSubmitting}>
                                                            {isSubmitting ? <SpinningCustomLogo size={20} color="inherit" /> : "Create Ticket"}
                                                        </Button>
                                                        <Tooltip title="Open your own email client">
                                                            <Button 
                                                                variant="outlined" 
                                                                onClick={() => handleEmailSubmit(values)}
                                                                startIcon={<EmailIcon />}
                                                                sx={{ borderRadius: '20px', px: 3, textTransform: 'none' }}
                                                            >
                                                                Send via Email
                                                            </Button>
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Form>
                                    )}
                                </Formik>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>
        </Container>
    );
};

export default HelpPage;
